<!-- Informational Alert -->
<div *ngIf="isDisabled" class="alert alert-info" role="alert">
  <i class="bi bi-info-circle me-2"></i>
  {{ infoMessage }}
</div>

<!-- Error Message -->
<div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
  <i class="bi bi-exclamation-triangle-fill me-2"></i>
  {{ errorMessage }}
  <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
</div>

<!-- Success Message -->
<div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
  <i class="bi bi-check-circle-fill me-2"></i>
  {{ successMessage }}
  <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
</div>

<!-- Loading -->
<div *ngIf="loading" class="text-center py-4">
  <div class="spinner-border spinner-border-sm text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <span class="ms-2 text-muted">Loading representatives...</span>
</div>

<!-- Representatives Content -->
<div *ngIf="!loading">
  <!-- Header with Add button -->
  <div class="d-flex align-items-center justify-content-between mb-3">
    <h6 class="mb-0">
      <i class="bi bi-people me-2"></i>
      Representatives
      <span class="badge bg-secondary ms-2">{{ representatives.length }}</span>
    </h6>
    <button
      class="btn btn-primary btn-sm"
      (click)="toggleAddForm()"
      [disabled]="isDisabled"
    >
      <i class="bi" [class.bi-plus]="!showAddForm" [class.bi-x]="showAddForm"></i>
      {{ showAddForm ? 'Cancel' : 'Add Representative' }}
    </button>
  </div>

  <!-- Add Representative Form -->
  <div *ngIf="showAddForm" class="card mb-3">
    <div class="card-body">
      <h6 class="mb-3">New Representative</h6>
      <div *ngIf="addFormError" class="alert alert-danger py-2 mb-3">
        {{ addFormError }}
      </div>
      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label">Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newRep.name"
            placeholder="Full name"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newRep.title"
            placeholder="e.g., Regional Sales Director (optional)"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Territory</label>
          <textarea
            class="form-control"
            [(ngModel)]="newRep.territory"
            placeholder="e.g., Iowa, Illinois, Missouri (optional)"
            rows="2"
          ></textarea>
        </div>
        <div class="col-md-4">
          <label class="form-label">Phone</label>
          <input
            type="tel"
            class="form-control"
            [(ngModel)]="newRep.phone"
            mask="(000) 000-0000"
            [validation]="false"
            placeholder="(optional)"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Email</label>
          <input
            type="email"
            class="form-control"
            [(ngModel)]="newRep.email"
            placeholder="(optional)"
          />
        </div>
      </div>
      <div class="mt-3">
        <button
          class="btn btn-primary btn-sm"
          (click)="submitRepresentative()"
          [disabled]="addingRep"
        >
          <span *ngIf="addingRep" class="spinner-border spinner-border-sm me-1" role="status"></span>
          {{ addingRep ? 'Creating...' : 'Create Representative' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="representatives.length === 0 && !showAddForm" class="text-center py-5 text-muted">
    <i class="bi bi-people fs-1 d-block mb-3"></i>
    <p>No representatives configured for this sponsor.</p>
    <button
      class="btn btn-outline-primary btn-sm"
      (click)="toggleAddForm()"
      [disabled]="isDisabled"
    >
      <i class="bi bi-plus me-1"></i>
      Add First Representative
    </button>
  </div>

  <!-- Representatives Table -->
  <div class="table-responsive" *ngIf="representatives.length > 0">
    <table class="table table-hover mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Title</th>
          <th>Territory</th>
          <th>Phone</th>
          <th>Email</th>
          <th class="text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let rep of representatives">
          <!-- Display Row -->
          <tr *ngIf="editingRepId !== rep.id">
            <td>{{ rep.name }}</td>
            <td>{{ rep.title || '---' }}</td>
            <td>{{ rep.territory || '---' }}</td>
            <td>{{ (rep.phone | phoneFormat) || '---' }}</td>
            <td>{{ rep.email || '---' }}</td>
            <td class="text-end">
              <div class="d-flex gap-2 justify-content-end">
                <button
                  class="btn btn-outline-primary btn-sm"
                  (click)="startEdit(rep)"
                  title="Edit representative"
                  [disabled]="isDisabled"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  class="btn btn-outline-danger btn-sm"
                  (click)="archiveRepresentative(rep)"
                  title="Archive representative"
                  [disabled]="isDisabled"
                >
                  <i class="bi bi-archive"></i>
                </button>
              </div>
            </td>
          </tr>

          <!-- Edit Row -->
          <tr *ngIf="editingRepId === rep.id" class="table-active">
            <td colspan="6">
              <div class="p-2">
                <h6 class="mb-3">Edit Representative</h6>
                <div *ngIf="editError" class="alert alert-danger py-2 mb-3">
                  {{ editError }}
                </div>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Name <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" [(ngModel)]="editData.name" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Title</label>
                    <input type="text" class="form-control" [(ngModel)]="editData.title" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Territory</label>
                    <textarea class="form-control" [(ngModel)]="editData.territory" rows="2"></textarea>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Phone</label>
                    <input type="tel" class="form-control" [(ngModel)]="editData.phone" mask="(000) 000-0000" [validation]="false" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Email</label>
                    <input type="email" class="form-control" [(ngModel)]="editData.email" />
                  </div>
                </div>
                <div class="mt-3 d-flex gap-2">
                  <button
                    class="btn btn-primary btn-sm"
                    (click)="saveEdit()"
                    [disabled]="savingEdit"
                  >
                    <span *ngIf="savingEdit" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    {{ savingEdit ? 'Saving...' : 'Save' }}
                  </button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="cancelEdit()">Cancel</button>
                </div>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>
</div>
